import { useCallback } from 'react';
import { extend, get, isEmpty } from 'lodash';
import { t } from '@lingui/macro';
import { i18n } from '@lingui/core';
import { HTTP_STATUS_400 } from '../../../../services/ApiDataService';

const ERROR_MESSAGES = {
    SERVER: i18n._(
        t`There was a problem communicating with the server.  If this issue persists, please contact support.`
    ),
    DELETE: i18n._(
        t`IP Address(es) was not deleted because of the following error: The selected item could not be deleted.`
    ),
};

const ERROR_CODES = {
    CANNOT_BE_DELETED: 'cannot_be_deleted',
};

/**
 * Check if error is a "cannot be deleted" validation error
 * @param {Object} errorObject - Error object from API
 * @returns {boolean} True if error is a deletion validation error
 */
const isDeleteValidationError = (errorObject) => {
    if (!errorObject?.getStatus || errorObject.getStatus() !== HTTP_STATUS_400) {
        return false;
    }

    const responseBody = get(errorObject, 'responseBody');
    if (isEmpty(responseBody)) {
        return false;
    }

    const message = get(responseBody, 'message');
    return message === ERROR_CODES.CANNOT_BE_DELETED;
};

/**
 * Custom hook for handling infrastructure delete errors
 * Provides consistent error handling for infrastructure deletion operations
 *
 * @returns {Function} Error handler function that throws enhanced error with user-friendly message
 *
 * @example
 * function InfrastructureList() {
 *   const handleDeleteError = useInfrastructureDeleteError();
 *
 *   const deleteItems = async (ids) => {
 *     try {
 *       await deleteApi(ids);
 *     } catch (error) {
 *       handleDeleteError(error); // Throws enhanced error with user-friendly message
 *     }
 *   };
 * }
 */
export function useInfrastructureDeleteError() {
    /**
     * Handle delete errors with specific logic for validation failures
     * @param {Object} errorObject - Error object from API response
     * @throws {Object} Extended error object with responseErrorMessage property
     */
    const handleDeleteError = useCallback((errorObject) => {
        const responseErrorMessage = isDeleteValidationError(errorObject)
            ? ERROR_MESSAGES.DELETE
            : ERROR_MESSAGES.SERVER;

        throw extend(errorObject, { responseErrorMessage });
    }, []);

    return handleDeleteError;
}
